import { useEffect, useState, lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { MsalProvider } from '@azure/msal-react';
import { FluentProvider } from '@fluentui/react-components';
import { msalInstance } from './config/msalConfig';
import { exchangeIdTokenForSession } from './services/sessionClient';
import { htsTheme, htsDarkTheme } from './themes/htsTheme';
import { useResolvedTheme } from './themes/themeMode';
import { useDeviceDetection } from './hooks/useDeviceDetection';
import { ErrorBoundary } from './components/layout/ErrorBoundary';
import { RouteErrorElement } from './components/layout/RouteErrorElement';
import { LoginPage } from './components/auth/LoginPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Layout } from './components/layout/Layout';
import { NativeRuntime } from './components/NativeRuntime';
import { AppSkeleton } from './components/layout/AppSkeleton';
import { FeedbackProvider } from './components/feedback/FeedbackProvider';
import { PwaUpdatePrompt } from './components/PwaUpdatePrompt';

/**
 * LoginRoute — the login standard (hts-platform/docs/login-screen-standard.md)
 * forces the navy page + white card in BOTH modes. Per Sage's sign-off
 * ("the logins for all repos should stay the same as it was in project
 * portal with the darker blues"), the login is intentionally identical
 * in light and dark mode across all 4 hubs. We pin the route to `htsTheme`
 * (the light theme) so the card stays white in dark mode; the rest of the
 * app uses the resolved theme via its own FluentProvider.
 *
 * Regression #1 (controlled-chaos/notes/deep-visual-audit-ss.md) called out
 * that the original wrapper was hardcoded to `htsTheme`. The first iteration
 * made it conditional, but that flipped the card to a dark surface in dark
 * mode — which Sage rejected. Pinning to light is the right answer.
 */
function LoginRoute() {
  // Session auto-return: with the 15-day cookie + restored MSAL account, a
  // user whose ProtectedRoute bounced mid-boot (or who navigated to /login
  // directly) shouldn't have to click Microsoft again — send them straight
  // in. LoginPage keeps its normal flow for genuinely signed-out users.
  const { isAuthenticated, isLoading } = useAuth();
  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return (
    <FluentProvider theme={htsTheme}>
      <LoginPage />
    </FluentProvider>
  );
}
import { initConsoleCapture } from './utils/consoleCapture';
import { validateRedirectPath } from './utils/redirectValidation';
import { usePageViewTracking } from './hooks/usePageViewTracking';

// Route pages are lazy-loaded so a field iPad only downloads the code for the
// page it opens. The heavy survey/export pages (jsPDF, ExcelJS, pdfjs, canvas)
// no longer inflate the initial bundle served on login/home. LoginPage stays
// eager as the entry point.
const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage })));
const DevicesPage = lazy(() => import('./pages/DevicesPage').then((m) => ({ default: m.DevicesPage })));
const SecuritySurveyPage = lazy(() => import('./pages/SecuritySurveyPage').then((m) => ({ default: m.SecuritySurveyPage })));
const InstallHomePage = lazy(() => import('./pages/install/InstallHomePage').then((m) => ({ default: m.InstallHomePage })));
const InstallProjectPage = lazy(() => import('./pages/install/InstallProjectPage').then((m) => ({ default: m.InstallProjectPage })));
const P2PDashboardPage = lazy(() => import('./pages/P2PDashboardPage').then((m) => ({ default: m.P2PDashboardPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const FolderPage = lazy(() => import('./pages/FolderPage').then((m) => ({ default: m.FolderPage })));
const TrashPage = lazy(() => import('./pages/TrashPage').then((m) => ({ default: m.TrashPage })));
const AssetViewerPage = lazy(() => import('./pages/AssetViewerPage').then((m) => ({ default: m.AssetViewerPage })));
const P2PRequestPage = lazy(() => import('./pages/P2PRequestPage').then((m) => ({ default: m.P2PRequestPage })));
const FwPreviewPage = lazy(() => import('./pages/FwPreviewPage'));
const FwImportPage = lazy(() => import('./pages/FwImportPage'));

// Dev-only Fieldwire markup preview harness (visual check for the migration
// converter/renderer). Registered only under the local dev auth bypass.
const fwPreviewRoute = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
  ? [{ path: '/fw-preview', element: <FwPreviewPage /> }]
  : [];

// Fieldwire import driver (one-shot cutover: backup → wipe → import).
// Build-flag gated, like the #239 backfill page.
const fwImportRoute = import.meta.env.VITE_ENABLE_FIELDWIRE_IMPORT === 'true'
  ? [{ path: '/fw-import', element: <ProtectedRoute><Layout><FwImportPage /></Layout></ProtectedRoute> }]
  : [];

/** Shell-shaped skeleton shown while a lazily-loaded route chunk is fetched. */
function PageFallback() {
  return <AppSkeleton />;
}

/**
 * Root layout that provides a single FeedbackProvider instance across all routes.
 * This ensures the wizard state persists during navigation. The Suspense
 * boundary here covers all lazily-loaded route pages rendered into the Outlet.
 */
function FeedbackRoot() {
  usePageViewTracking();
  return (
    <FeedbackProvider>
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
    </FeedbackProvider>
  );
}

const router = createBrowserRouter([
  // Login cards force light tokens in both modes (login-screen standard).
  { path: '/login', element: <LoginRoute /> },
  {
    element: <FeedbackRoot />,
    // Route render/lazy errors are caught by the router, not by the
    // App-level ErrorBoundary — without this they render the router's raw
    // default error page (blank in production).
    errorElement: <RouteErrorElement />,
    children: [
      {
        path: '/',
        element: <ProtectedRoute><Layout><HomePage /></Layout></ProtectedRoute>,
      },
      {
        path: '/projects',
        element: <ProtectedRoute><Layout><ProjectsPage /></Layout></ProtectedRoute>,
      },
      {
        path: '/folders/:folderId',
        element: <ProtectedRoute><Layout><FolderPage /></Layout></ProtectedRoute>,
      },
      {
        path: '/folders/:folderId/assets/:assetId',
        element: <ProtectedRoute><Layout hideNavigation noPadding immersive><AssetViewerPage /></Layout></ProtectedRoute>,
      },
      {
        // Resumable P2P request — a project-like page with autosaved drafts,
        // deep-linked from the folder's "+ Add P2P Request".
        path: '/folders/:folderId/p2p/new',
        element: <ProtectedRoute><Layout noPadding><P2PRequestPage /></Layout></ProtectedRoute>,
      },
      {
        path: '/trash',
        element: <ProtectedRoute><Layout><TrashPage /></Layout></ProtectedRoute>,
      },
      {
        path: '/devices',
        element: <ProtectedRoute><Layout><DevicesPage /></Layout></ProtectedRoute>,
      },
      {
        path: '/install',
        element: <ProtectedRoute requiredRole="Installer"><Layout hideNavigation noPadding><InstallHomePage /></Layout></ProtectedRoute>,
      },
      {
        path: '/install/:id',
        element: <ProtectedRoute requiredRole="Installer"><Layout hideNavigation noPadding><InstallProjectPage /></Layout></ProtectedRoute>,
      },
      {
        path: '/p2p',
        element: <ProtectedRoute><Layout><P2PDashboardPage /></Layout></ProtectedRoute>,
      },
      {
        path: '/projects/:id/survey',
        element: <ProtectedRoute><Layout hideNavigation noPadding immersive><SecuritySurveyPage /></Layout></ProtectedRoute>,
      },
      ...fwPreviewRoute,
      ...fwImportRoute,
    ],
  },
  // 404 — regression #10 in controlled-chaos/notes/deep-visual-audit-ss.md.
  // The previous catch-all `<Navigate to="/" replace />` silently sent users
  // to home, hiding broken-link bugs. Now we render a real 404 page that
  // uses the EmptyState contract (Series 5). We intentionally render it
  // outside the Layout so the user sees a clean "page not found" instead
  // of a half-broken chrome with header/rail.
  { path: '*', element: <NotFoundPage /> },
]);

function App() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const resolvedTheme = useResolvedTheme();
  const theme = resolvedTheme === 'dark' ? htsDarkTheme : htsTheme;
  // Subscribe at the root so the `hts-touch` body class is in place
  // before any route renders. (index.css also reads `@media (pointer:
  // coarse)` directly — the body class is a fallback for hosts that
  // don't surface the media query reliably.)
  useDeviceDetection();

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();

        // Handle redirect promise and get the response
        const response = await msalInstance.handleRedirectPromise();

        if (response) {
          // User has returned from an explicit Microsoft login (handleRedirectPromise
          // resolves non-null ONLY on that return trip — never on a plain load — so
          // this exchange cannot silently re-auth a stale cached account, #469).
          msalInstance.setActiveAccount(response.account);

          // Exchange the MSAL id_token for the app session (httpOnly cookie)
          // before navigating anywhere — API calls authorize on that cookie now.
          if (response.idToken) {
            try {
              await exchangeIdTokenForSession(response.idToken);
            } catch (exchangeError) {
              console.error('Session exchange after Microsoft login failed:', exchangeError);
              setInitError(
                exchangeError instanceof Error
                  ? `Sign-in could not be completed: ${exchangeError.message}`
                  : 'Sign-in could not be completed. Please try again.'
              );
              return;
            }
          }

          // Get the intended destination from session storage, validated against allowlist
          const rawPath = sessionStorage.getItem('authRedirectPath');
          sessionStorage.removeItem('authRedirectPath');
          const intendedPath = validateRedirectPath(rawPath);

          // Navigate to intended path after a brief delay to allow state to settle
          setTimeout(() => {
            window.location.href = intendedPath;
          }, 100);
        }

        initConsoleCapture();

        setIsInitialized(true);
      } catch (error) {
        console.error('MSAL initialization error:', error);
        setInitError(
          error instanceof Error ? error.message : 'Authentication service failed to initialize'
        );
      }
    };

    initializeMsal();
  }, []);

  if (initError) {
    return (
      <FluentProvider theme={theme}>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: '100vh', padding: '24px', fontFamily: 'var(--hts-font-body)', textAlign: 'center',
          backgroundColor: 'var(--hts-app-bg)', color: 'var(--hts-ink)',
        }}>
          <h2 style={{ marginBottom: '8px', color: 'var(--hts-error-strong)' }}>Authentication Failed</h2>
          <p style={{ color: 'var(--hts-ink-secondary)', marginBottom: '16px', maxWidth: '500px' }}>
            The authentication service could not start. This is usually caused by a network issue
            or a misconfigured browser. Try reloading the page.
          </p>
          <p style={{ color: 'var(--hts-ink-muted)', fontSize: '12px', marginBottom: '24px', fontFamily: 'var(--hts-font-mono)', maxWidth: '500px', wordBreak: 'break-word' }}>
            {initError}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px', fontSize: '14px', cursor: 'pointer',
              backgroundColor: 'var(--hts-primary-navy)', color: 'white', border: 'none', borderRadius: '4px',
            }}
          >
            Reload Page
          </button>
        </div>
      </FluentProvider>
    );
  }

  if (!isInitialized) {
    return (
      <FluentProvider theme={theme}>
        <AppSkeleton />
      </FluentProvider>
    );
  }

  return (
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <FluentProvider theme={theme}>
          {/* Native-shell glue (no-op in browsers): status-bar chrome +
              appStateChange drain seam for the offline upload queue. */}
          <NativeRuntime />
          <RouterProvider router={router} />
          <PwaUpdatePrompt />
        </FluentProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
}

export default App;